<!--
  CollectorDayPage — /collector/ — «يومك», the collector's day (collector-app
  phase 1, handoff spec Section 2.1; mockup Main.dc.html).

  Reads ONE document: collectorDayStore.day, the `collector-plans` show the
  backend built for me (brief 2026-09-30-collector-day-read). Every state on
  this page comes from it — a shop's chip maps the backend's derived `state`
  (collector/dayView.js), and «خلّصت» is never decided here.

  Top to bottom: the indigo header («يومك», the date, the batch, the leader),
  «دلوقتي عندك» (the first shop in progress, else the first not started — the
  whole card opens it), the cash with me, the progress bar, then my shops in
  the leader's route order. Every shop row opens its shop: a collector goes
  back to a finished shop to correct it, so done rows stay tappable too.
  Mockup footer «و6 محلات كمان تحت» is not drawn: the list simply continues.

  HER ANSWERS (collector-app P3-C, L-G): when a read finds a question she has
  newly answered, one green or amber card — «العضوة ردّت على «…»: {instruction}»
  with «افتح {shop}» — joins the notices above the cash, kept until dismissed.
  No sound, no push: he finds it the next time he looks at his day.

  ON THE PHONE (collector-app P2-B): the day and the taps come back from the
  phone before any request, so with no signal this page is the morning's day
  plus every tap kept; «آخر تحديث من الليدر HH:MM» is the last read the server
  answered (L-E). While the login is out the card «افتح يومك من تيليجرام»
  leads back in (L-D). Inside the Telegram Mini App the page is ONE screen,
  «افتح يومك في كروم» (L-A): the day does not live there.

  ROUTE INVARIANTS (playbook Section 5): EAGER `component:` (row 16),
  requireCollector (no membership tier) with App.vue's EAGER_COLLECTOR_PREFIX
  covering the cold hard-load guard skip (row 22), `meta.hideTabbar: true`.
-->
<template>
  <f7-page name="collector-day" class="collector-surface collector-day" :page-content="false">
    <f7-page-content :ptr="!inTma" :ptr-mousewheel="!inTma" @ptr:refresh="onPtr">
      <collector-tma-screen v-if="inTma"></collector-tma-screen>
      <template v-else>
        <header class="c-day-head" :class="{ 'c-day-head--overlap': nowShop && online && !paused }">
          <div class="c-day-head__row">
            <h1 class="c-day-head__title">
              {{ t('collector.day.title') }}
            </h1>
            <collector-sync-pill></collector-sync-pill>
          </div>
          <div v-if="sublineText" class="c-day-head__sub">
            {{ sublineText }}
          </div>
          <div v-if="plans.length > 1" class="c-day-head__plans">
            <button
              v-for="row in plans"
              :key="row.id"
              type="button"
              class="c-reset c-day-head__plan"
              :class="{ 'c-day-head__plan--on': row.id === activePlanId }"
              @click="dayStore.selectPlan(row.id)"
            >
              {{ t('collector.day.batch', { batch: row.plan?.batch }) }}
            </button>
          </div>
          <collector-prewarm-line></collector-prewarm-line>
        </header>

        <div v-if="!online" class="c-offline-banner">
          {{ t('collector.offline.banner') }}
        </div>

        <collector-auth-card></collector-auth-card>

        <template v-if="day">
          <button
            v-if="nowShop"
            type="button"
            class="c-reset c-now"
            :class="{ 'c-now--flat': !online || paused }"
            @click="openShop(nowShop)"
          >
            <span class="c-now__eyebrow">{{ t('collector.now.eyebrow') }}</span>
            <span class="c-now__row">
              <span class="c-now__text">
                <span class="c-now__name">{{ shopName(nowShop) }}</span>
                <span class="c-now__sub">
                  <template v-for="(part, index) in nowSubline" :key="index">
                    <template v-if="index > 0"> · </template><span class="c-piece">{{ part }}</span>
                  </template>
                </span>
              </span>
              <span class="c-now__open">
                <span>{{ t('collector.now.open') }}</span>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                  class="c-flip-rtl"
                ><path d="M15 6l-6 6 6 6" /></svg>
              </span>
            </span>
          </button>

          <collector-answer-cards @open-shop="openShopByKey"></collector-answer-cards>

          <collector-conflict-cards @open-shop="openShopByKey"></collector-conflict-cards>

          <section class="c-cash" :class="{ 'c-cash--short': money.shortCents > 0 }">
            <div class="c-cash__main">
              <div class="c-cash__label">
                {{ t('collector.cash.withMe') }}
              </div>
              <div class="c-cash__amount c-num">
                {{ pounds(day.cash?.withMeCents) }}
                <span class="c-cash__unit">{{ t('collector.cash.unit') }}</span>
              </div>
            </div>
            <div class="c-cash__side c-num">
              <div>
                {{ t('collector.cash.allocated') }}
                <strong>{{ pounds(day.cash?.allocatedCents) }}</strong>
              </div>
              <div>
                {{ t('collector.cash.paid') }}
                <strong>{{ pounds(paidAtShops) }}</strong>
              </div>
            </div>
            <div v-if="actions.pendingCount > 0" class="c-cash__note">
              {{ t('collector.cash.pendingNote') }}
            </div>
            <div v-if="money.remainingCents > 0" class="c-cash__need c-num">
              <i18n-t keypath="collector.cash.needRest" tag="span" scope="global">
                <template #amount>
                  <strong>{{ pounds(money.remainingCents) }}</strong>
                </template>
              </i18n-t>
              <!-- with no allocation recorded the phone does not know his cash: it says neither -->
              <template v-if="money.cashKnown">
                <span v-if="money.shortCents > 0" class="c-chip c-chip--amber">{{ t('collector.cash.short', { short: pounds(money.shortCents) }) }}</span>
                <span v-else class="c-chip c-chip--green">{{ t('collector.cash.enough') }}</span>
              </template>
            </div>
          </section>

          <section class="c-progress">
            <div class="c-progress__bar" role="presentation">
              <div class="c-progress__seg c-progress__seg--green" :style="{ width: `${segments.collected}%` }" />
              <div class="c-progress__seg c-progress__seg--red" :style="{ width: `${segments.oos}%` }" />
            </div>
            <div class="c-progress__legend">
              <div class="c-progress__counts">
                <span><i class="c-dot c-dot--green" />{{ t('collector.progress.collected', { n: progress.collected ?? 0 }) }}</span>
                <span><i class="c-dot c-dot--red" />{{ t('collector.progress.oos', { n: progress.oos ?? 0 }) }}</span>
                <span><i class="c-dot c-dot--grey" />{{ t('collector.progress.rest', { n: progress.pending ?? 0 }) }}</span>
              </div>
              <div class="c-progress__shops">
                {{ t('collector.progress.shopsDone', { done: progress.shopsDone ?? 0, total: progress.shopsTotal ?? shops.length }) }}
              </div>
            </div>
          </section>

          <div class="c-shops-head">
            <h2 class="c-shops-head__title">
              {{ t('collector.shops.heading') }}
            </h2>
            <span class="c-shops-head__order">{{ t('collector.shops.order') }}</span>
          </div>

          <div class="c-shops">
            <button
              v-for="shop in shops"
              :key="shopKey(shop)"
              type="button"
              class="c-reset c-shop-row"
              :class="`c-shop-row--${shopMarker(shop)}`"
              :data-state="shop.state"
              @click="openShop(shop)"
            >
              <span class="c-shop-row__marker" :class="`c-shop-row__marker--${shopMarker(shop)}`">
                <svg
                  v-if="shopMarker(shop) === 'done'"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                ><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                <svg
                  v-else-if="shopMarker(shop) === 'closed'"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                ><rect x="5"
                  y="10"
                  width="14"
                  height="10"
                  rx="2"
                /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>
                <span v-else class="c-num">{{ shop.position }}</span>
              </span>
              <span class="c-shop-row__text">
                <span class="c-shop-row__title">{{ shopRowTitle(shop) }}</span>
                <span v-if="shop.paidByWallet" class="c-shop-row__wallet">{{ t('collector.shopRow.walletShop') }}</span>
                <span class="c-shop-row__sub">
                  <template v-for="(part, index) in shopSubline(shop)" :key="index">
                    <template v-if="index > 0"> · </template><span class="c-piece">{{ part }}</span>
                  </template>
                </span>
              </span>
              <span v-if="shopRowMoney(shop) || shopStateChip(shop) || returnsChip(shop) || waitingText(shop)" class="c-shop-row__chips">
                <span v-if="shopRowMoney(shop)" class="c-shop-row__money c-num">
                  <span class="c-shop-row__money-label">{{ t(shopRowMoney(shop).labelKey) }}</span>
                  {{ pounds(shopRowMoney(shop).cents) }}
                </span>
                <span
                  v-if="shopStateChip(shop)"
                  class="c-chip"
                  :class="`c-chip--${shopStateChip(shop).tone}`"
                >{{ chipText(shopStateChip(shop)) }}</span>
                <span
                  v-if="returnsChip(shop)"
                  class="c-chip c-chip--amber"
                >{{ t(returnsChip(shop).key, returnsChip(shop).params) }}</span>
                <span v-if="waitingText(shop)" class="c-shop-row__waiting">{{ waitingText(shop) }}</span>
              </span>
            </button>
          </div>
          <button
            v-if="canSearchShops"
            type="button"
            class="c-reset c-day-shops-link"
            @click="shopSearchOpen = true"
          >
            {{ t('collector.preview.shopsLink') }}
          </button>
        </template>

        <div v-else class="c-day-state">
          <p>{{ stateText }}</p>
          <button
            v-if="canSearchShops && dayStore.status === 'empty'"
            type="button"
            class="c-reset c-day-shops-link"
            @click="shopSearchOpen = true"
          >
            {{ t('collector.preview.shopsLink') }}
          </button>
          <f7-button
            v-if="dayStore.status === 'error'"
            outline
            round
            @click="dayStore.load()"
          >
            {{ t('collector.day.retry') }}
          </f7-button>
        </div>
      </template>
    </f7-page-content>
    <collector-shop-search-sheet
      v-if="canSearchShops"
      v-model:opened="shopSearchOpen"
      @pick="onPickShop"
    ></collector-shop-search-sheet>
  </f7-page>
</template>

<script setup>
import '@/collector/collector.css';
import { useDocumentVisibility } from '@vueuse/core';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCollectorActionsStore } from '@/collector/actions.js';
import {
  countPhrase,
  currentShop,
  formatClock,
  formatPlanDate,
  paidAtShopsCents,
  phraseText,
  progressSegments,
  returnsChip,
  shopMarker,
  shopStateChip,
  shopSublineParts,
  signedPounds,
} from '@/collector/dayView.js';
import { dayMoney, shopMoney, shopRowMoney } from '@/collector/moneyView.js';
import { useCollectorNetwork } from '@/collector/network.js';
import { leadStatuses } from '@/collector/previewLeads.js';
import { authPaused } from '@/collector/session.js';
import CollectorAnswerCards from '@/components/collector/CollectorAnswerCards.vue';
import CollectorAuthCard from '@/components/collector/CollectorAuthCard.vue';
import CollectorConflictCards from '@/components/collector/CollectorConflictCards.vue';
import CollectorPrewarmLine from '@/components/collector/CollectorPrewarmLine.vue';
import CollectorShopSearchSheet from '@/components/collector/CollectorShopSearchSheet.vue';
import CollectorSyncPill from '@/components/collector/CollectorSyncPill.vue';
import CollectorTmaScreen from '@/components/collector/CollectorTmaScreen.vue';
import { confirmAndEnter } from '@/composables/previewConfirm.js';
import { useCurrency } from '@/composables/useCurrency.js';
import { useSupplierPreview } from '@/composables/useSupplierPreview.js';
import { isTma } from '@/platform/index.js';
import { useAuthStore } from '@/stores/authStore.js';
import { shopKey, useCollectorDayStore } from '@/stores/collectorDayStore.js';
import { previewReadyAt, useCollectorPrewarmStore } from '@/stores/collectorPrewarmStore.js';

const props = defineProps({
  f7router: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const { formatAmount } = useCurrency();
const dayStore = useCollectorDayStore();
// «عرض جاهز HH:MM» on a shop row: when its supplier-preview copy was saved;
// then its offline «المحل مهتم» (collector/previewLeads.js `leadStatuses`).
const prewarm = useCollectorPrewarmStore();
const actions = useCollectorActionsStore();
// Read once: isTma() re-checks several signals on every call (L-A screen).
const inTma = isTma();
// «عروض المحلات» — the door to a shop's supplier preview that needs no plan
// (preview-shop-search). A collector only, never in the Mini App.
const authStore = useAuthStore();
const preview = useSupplierPreview();
const shopSearchOpen = ref(false);
const canSearchShops = computed(() => !inTma && authStore.isCollector === true);
function onPickShop(shop) {
  confirmAndEnter(preview.enter, shop);
}
const network = inTma ? null : useCollectorNetwork();
const online = computed(() => network?.isOnline.value === true);
// Under the re-entry card the «دلوقتي عندك» card sits below it, not over the header.
const paused = computed(() => authPaused.value === true);

/** Whole pounds as the paper prints them: «2,480», «2,480.50». */
const pounds = (cents) => formatAmount(cents, { trimZeros: true });

const day = computed(() => dayStore.day);
const plans = computed(() => dayStore.plans);
const activePlanId = computed(() => dayStore.activePlanId);
const shops = computed(() => dayStore.shops);
const progress = computed(() => day.value?.progress ?? {});
const segments = computed(() => progressSegments(day.value?.progress));
const paidAtShops = computed(() => paidAtShopsCents(day.value?.cash));
const nowShop = computed(() => currentShop(day.value));
// What the shops still ahead need against the cash on him — read off the rebased day.
const money = computed(() => dayMoney(day.value));

const sublineText = computed(() => {
  const plan = day.value?.plan;
  if (!plan) return '';
  const date = formatPlanDate(plan.date) ?? '';
  // Offline the collector needs to know how old his picture of the day is:
  // the last read the server answered (`fetchedAt` on the phone — L-E).
  const updated = formatClock(dayStore.lastSyncedAt);
  if (!online.value && updated) {
    return t('collector.day.sublineOffline', { date, batch: plan.batch, time: updated });
  }
  return plan.leaderName
    ? t('collector.day.subline', { date, batch: plan.batch, leader: plan.leaderName })
    : t('collector.day.sublineNoLeader', { date, batch: plan.batch });
});

const stateText = computed(() => {
  if (dayStore.status === 'empty') return t('collector.day.empty');
  if (dayStore.status === 'error') return t('collector.day.error');
  return t('collector.day.loading');
});

function shopName(shop) {
  return shop?.name ?? t('collector.shops.noShop');
}

/** Done and closed rows carry the number in the title (the circle shows an icon). */
function shopRowTitle(shop) {
  const marker = shopMarker(shop);
  if (marker === 'done' || marker === 'closed') {
    return t('collector.shopRow.title', { position: shop.position, name: shopName(shop) });
  }
  return shopName(shop);
}

/** The pieces of a shop row's sub-line; the template joins them with « · ». */
function shopSubline(shop) {
  return shopSublineParts(shop, {
    previewReadyAt: previewReadyAt(prewarm.copies, shop?.supplierId),
    leadStatus: shop?.supplierId == null ? null : leadStatuses.value[String(shop.supplierId)],
  }).map((part) => phraseText(part, t, pounds));
}

function chipText(chip) {
  if (chip.diffCents != null) {
    return t(chip.key, { amount: signedPounds(chip.diffCents) });
  }
  return t(chip.key);
}

/** «المحل 3 من 4 · باقي صنفين · محتاج 460 ج.م» — what is left there and what it costs, as its pieces. */
const nowSubline = computed(() => {
  const shop = nowShop.value;
  if (!shop) return [];
  const counters = shop.counters ?? {};
  const parts = [t('collector.now.position', { position: shop.position, total: shops.value.length })];
  if (counters.pending > 0) {
    parts.push(phraseText({ key: 'collector.now.remaining', nested: countPhrase('items', counters.pending) }, t, pounds));
  } else {
    parts.push(phraseText(countPhrase('items', counters.total ?? 0), t, pounds));
  }
  const { remainingCents } = shopMoney(shop);
  if (remainingCents > 0) parts.push(t('collector.now.needed', { amount: pounds(remainingCents) }));
  return parts;
});

function openShop(shop) {
  openShopByKey(shopKey(shop));
}

/**
 * `planId`: the plan a card is about, when it is one of today's — a card can be
 * about the plan that is NOT on screen (an invoice refused hours later).
 */
function openShopByKey(key, planId = null) {
  const listed = planId != null && plans.value.some((row) => String(row.id) === String(planId));
  const plan = listed ? String(planId) : activePlanId.value;
  const query = plans.value.length > 1 && plan ? `?plan=${plan}` : '';
  props.f7router?.navigate(`/collector/shops/${key}/${query}`);
}

/** «3 علامات مستنية» under a shop's chip while its taps have not been answered. */
function waitingText(shop) {
  const waiting = actions.waitingByShop[shopKey(shop)] ?? 0;
  if (waiting === 0 || online.value) return '';
  const phrase = countPhrase('waitingMarks', waiting);
  return t(phrase.key, phrase.params);
}

async function onPtr(done) {
  if (inTma) {
    done();
    return;
  }
  try {
    await dayStore.load();
  } finally {
    done();
  }
}

// F7 removes this page when a shop is pushed over it, so coming back remounts
// it: every visit reads today's plans and the day again (an unchanged day is a
// 304). A phone brought back to the foreground re-reads too.
onMounted(() => {
  if (inTma) return;
  actions.start();
  dayStore.load();
});

const visibility = useDocumentVisibility();
watch(visibility, (state) => {
  if (state === 'visible' && !inTma) dayStore.refresh();
});
</script>

<style scoped>
.c-day-head {
  background: var(--c-indigo);
  padding: calc(var(--f7-safe-area-top, 0px) + 22px) 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.c-day-head--overlap {
  padding-bottom: 40px;
}

.c-day-head__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.c-day-head__title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
}

.c-day-head__sub {
  font-size: 14px;
  color: var(--c-head-sub);
}

.c-day-head__plans {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.c-day-head__plan {
  min-height: 36px;
  padding: 0 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
}

.c-day-head__plan--on {
  background: #ffffff;
  color: var(--c-indigo);
}

.c-now {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: calc(100% - 32px);
  margin: -26px 16px 0;
  padding: 14px 16px;
  background: var(--c-card);
  border: 2px solid var(--c-purple);
  border-radius: var(--c-radius-card);
  box-shadow: 0 8px 22px rgba(49, 0, 111, 0.12);
  box-sizing: border-box;
}

.c-now__eyebrow {
  font-size: 12px;
  font-weight: 700;
  color: var(--c-indigo);
}

.c-now__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.c-now__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.c-now__name {
  font-size: 24px;
  font-weight: 700;
}

.c-now__sub {
  font-size: 13px;
  color: var(--c-muted);
}

.c-now__open {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 18px;
  border-radius: var(--c-radius-button);
  background: var(--c-indigo);
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
}

.c-cash {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin: 12px 16px 0;
  padding: 12px 16px;
  background: var(--c-card);
  border: 1px solid var(--c-border);
  border-radius: var(--c-radius-card);
}

/* The cash does not cover the shops ahead: advisory, never blocks a tap. */
.c-cash--short {
  border: 2px solid var(--c-amber-border);
}

.c-cash__need {
  display: flex;
  flex-basis: 100%;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--c-border);
  font-size: 14px;
}

.c-cash__need strong {
  font-size: 17px;
  font-weight: 700;
}

.c-cash__note {
  flex-basis: 100%;
  font-size: 12px;
  color: var(--c-amber-text);
}

.c-offline-banner {
  padding: 10px 16px;
  background: var(--c-amber-fill);
  border-bottom: 1px solid var(--c-amber-border);
  color: var(--c-amber-ink);
  font-size: 13px;
  font-weight: 500;
}

/* Under the offline banner the «دلوقتي عندك» card sits below it, not over the header. */
.c-now--flat {
  margin-top: 12px;
}

.c-shop-row__waiting {
  font-size: 11px;
  font-weight: 700;
  color: var(--c-amber-pill);
}

.c-cash__main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-grow: 1;
}

.c-cash__label {
  font-size: 12px;
  font-weight: 500;
  color: var(--c-muted);
}

.c-cash__amount {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
}

.c-cash__unit {
  font-size: 13px;
  font-weight: 500;
  color: var(--c-muted);
}

.c-cash__side {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: var(--c-muted);
  text-align: end;
}

.c-cash__side strong {
  color: var(--c-ink);
}

.c-progress {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 10px 16px 0;
  padding: 0 4px;
}

.c-progress__bar {
  display: flex;
  gap: 2px;
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  background: var(--c-border);
}

.c-progress__seg--green {
  background: var(--c-green-text);
}

.c-progress__seg--red {
  background: var(--c-red-text);
}

.c-progress__legend {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 12px;
  color: var(--c-muted);
}

.c-progress__counts {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.c-progress__shops {
  font-weight: 700;
  color: var(--c-ink);
  white-space: nowrap;
}

.c-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-inline-end: 4px;
}

.c-dot--green {
  background: var(--c-green-text);
}

.c-dot--red {
  background: var(--c-red-text);
}

.c-dot--grey {
  background: var(--c-dot);
}

.c-shops-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin: 18px 16px 8px;
}

.c-shops-head__title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
}

.c-shops-head__order {
  font-size: 12px;
  color: var(--c-muted);
}

.c-shops {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 16px;
  padding-bottom: calc(var(--f7-safe-area-bottom, 0px) + 24px);
}

.c-shop-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 56px;
  padding: 12px 14px;
  background: var(--c-card);
  border: 1px solid var(--c-border);
  border-radius: var(--c-radius-row);
  box-sizing: border-box;
}

.c-shop-row[data-state='done_matched'],
.c-shop-row[data-state='done_no_paper'] {
  border-color: var(--c-green-border);
}

.c-shop-row[data-state='done_diff'] {
  border-color: var(--c-amber-border);
}

.c-shop-row--current {
  border: 2px solid var(--c-purple);
}

.c-shop-row__marker {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 14px;
  font-weight: 700;
  background: var(--c-ground);
  color: var(--c-muted);
}

.c-shop-row__marker--done {
  background: var(--c-green-fill);
  color: var(--c-green-text);
}

.c-shop-row__marker--closed {
  background: var(--c-border);
  color: var(--c-ink-soft);
}

.c-shop-row__marker--current {
  background: var(--c-indigo);
  color: #ffffff;
}

.c-shop-row__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-grow: 1;
  min-width: 0;
}

.c-shop-row__wallet {
  display: block;
  color: var(--poolit-warning-600, #b45309);
  font-size: 12px;
  font-weight: 600;
}

.c-shop-row__title {
  font-size: 15px;
  font-weight: 700;
}

.c-shop-row__sub {
  font-size: 12px;
  color: var(--c-muted);
}

/* one piece of a sub-line («محتاج 2,255 ج.م»): a wrap falls between pieces, and
   a piece wider than the line still wraps inside itself instead of spilling */
.c-piece { display: inline-block; }

.c-shop-row__chips {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  gap: 4px;
}

.c-shop-row__money {
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
}

.c-shop-row__money-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--c-muted);
}

.c-day-shops-link {
  display: block;
  margin: 8px auto 24px;
  padding: 12px 16px;
  min-height: 44px;
  color: var(--c-indigo, var(--f7-theme-color));
  font-size: 15px;
  font-weight: 600;
  text-decoration: underline;
}

.c-day-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 16px;
  color: var(--c-muted);
  text-align: center;
}
</style>

<!-- non-scoped: the arrow points the way of travel, which flips with the
     reading direction on <html> (CLAUDE.md: never :global(ancestor) .child). -->
<style>
[dir='rtl'] .collector-day .c-flip-rtl {
  transform: scaleX(1);
}

[dir='ltr'] .collector-day .c-flip-rtl {
  transform: scaleX(-1);
}
</style>
